import { CommonApiService } from '@services/common-api-service';
import { ConfigService } from '@services/config-service';
import { addCardResponse, loginController } from '@services/login-controller';
import { Component, Element, EventEmitter, Host, Prop, State, Event as StencilEvent, Watch, h } from '@stencil/core';
import { pathStore } from '@store/path-store';
import { If } from '@utils/If';
import { formatNumberIN } from '@utils/utilities';
import { CARD_SWITCH_FAILED_MESSAGE } from '../../../config';

@Component({
  tag: 'card-details',
  styleUrl: 'card-details.scss',
  shadow: true
})
export class CardDetails {
  @Prop() assetPath: string;
  @Prop() cardsAvailable: any = [];
  @Prop() scrolled: boolean = false;
  @Prop() headerTransparent: boolean = false;
  @Prop() selectedCard: any = {};
  @Prop() selectedCardName: string = '';
  @Prop() selectedCardPoints: string = '';
  @Prop() selectedCardLastFourDigits: string = '';
  @Prop() customerInfo: any = {};
  @Prop() activeProgram: string = '';
  @Prop() addCardEventName: string = 'show-add-card-with-session';
  @Prop() switchEnabled: boolean = true;
  @State() emitAddCardToParent: boolean = true;
  @State() showCards: boolean = false;
  @State() addCardTitle: string = '';
  @State() hasScrolled: boolean = false;
  @State() showAddCardPopup: boolean = false;
  @State() availableCards: any = [];
  @State() isAddCardRequired: boolean = false;
  @StencilEvent() selectCard: EventEmitter<any>;
  @Element() hostElement: HTMLElement;
  cardFaciaUrl: string = ConfigService.cardFaciaCdnUrl || '';
  private apiService = new CommonApiService();
  pointsFetched: boolean = false;
  private callbackFn: ((details: addCardResponse) => void) | null = null;

  private getLastFourDigits(): string {
    return this.selectedCard?.last4digit || this.selectedCardLastFourDigits || '----';
  }

  @Watch('showCards')
  watchShowCards(newValue: boolean) {
    if (typeof document === 'undefined') return;

    if (newValue) {
      document.addEventListener('click', this.handleClickOutside);
    } else {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }

  @Watch('scrolled')
  watchScrollChanges() {
    if (typeof document === 'undefined') return;
    this.hasScrolled = this.scrolled;
  }

  handleClickOutside = (event: MouseEvent) => {
    const clickedInside = this.hostElement.contains(event.target as Node);
    if (!clickedInside) {
      this.showCards = false;
    }
  };

  showCardList = async (event: MouseEvent) => {
    if (!this.switchEnabled) return;
    event.stopPropagation();
    this.showCards = !this.showCards;
    if (this.pointsFetched) return;
    try {
      const response = await this.apiService.post('/getCards', {
        loginId: this.customerInfo.loginId
      }
      );
      if (response.status) {
        this.availableCards = response.customerCards || [];
      }
      this.pointsFetched = true;
    } catch (error) {
      console.log(error);
    }
  };

  handleCardSelected = async (event: any) => {
    event.stopPropagation();
    const { detail = {} } = event;
    let cardType = detail?.cardType?.replaceAll('_', ' ') || '';
    cardType = cardType[0].toUpperCase() + cardType?.slice(1)?.toLowerCase();
    loginController.showLoader({
      title: 'Taking you to ' + cardType + ' experience...',
      cardFaciaUrl: detail.imageUrl || ''
    });
    this.showCards = false;
    try {
      const cardSelect = await this.apiService.post('/api/getcustomercardpoints',
        {
          ctype: 'flights',
          _token: this.customerInfo['XSRF-TOKEN'],
          takecard: detail.id
        }
      );
      if (cardSelect.status === true || cardSelect.status === "true") {
        this.selectCard.emit(event.detail);
      } else {
        loginController.showLoader({
          title: cardSelect?.message || CARD_SWITCH_FAILED_MESSAGE,
          cardFaciaUrl: '',
          subTitle: 'Taking you back!',
          status: 'failed'
        });
        setTimeout(() => {
          loginController.hideLoader();
        }, 3000);
      }
    } catch (error) {
      console.log(error);
      loginController.showLoader({
        title: CARD_SWITCH_FAILED_MESSAGE,
        cardFaciaUrl: '',
        subTitle: '',
        status: 'failed'
      });
      setTimeout(() => {
        loginController.hideLoader();
      }, 3000);
    }
  };

  addCardPopup = () => {
    this.showAddCardPopup = true;
  };

  onCancelAddCard = () => {
    this.showAddCardPopup = false;
    if (this.callbackFn) {
      this.callbackFn({ info: { authenticated: false } });
    }
  };

  onCloseAddCard = (event: any) => {
    const { detail = {} } = event;
    if (this.emitAddCardToParent) {
      this.selectCard.emit(detail);
    }
    if (this.callbackFn) {
      this.callbackFn({ info: { ...detail, authenticated: true } });
    }
    this.showAddCardPopup = false;
  };

  private handleAddCardEvent = (event: CustomEvent) => {
    const { callback, notifyParent = false, addCardTitle = '', isAddCardRequired = false } = event.detail || {};
    this.callbackFn = callback;
    this.emitAddCardToParent = notifyParent;
    this.showAddCardPopup = true;
    this.addCardTitle = addCardTitle;
    this.isAddCardRequired = isAddCardRequired;
  };

  connectedCallback() {
    if (typeof window !== 'undefined') {
      window.addEventListener(this.addCardEventName, this.handleAddCardEvent);
    }
  }

  disconnectedCallback() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(this.addCardEventName, this.handleAddCardEvent);
    }
  }

  onPoupClose = (event: any) => {
    event.stopPropagation();
    this.showCards = false;
  };

  onCardFaciaError = (event: any) => {
    if (event.target.dataset.fallback) return; // already tried fallback
    event.target.onerror = null;
    event.target.src = `${this.cardFaciaUrl}default.webp`;
    event.target.dataset.fallback = 'true';
  };

  componentWillLoad() {
    this.availableCards = this.cardsAvailable?.map((card: any) => ({ ...card, pointsLoading: true })) || [];
  }
  render() {
    const assetPath = pathStore.assetPath;
    return (
      <Host>
        <linear-gradrient>
          <div class="relative text-white">
            <If
              condition={this.availableCards.length === 0 && (!this.selectedCardName || this.selectedCardName === 'undefined')}
              children={
                <div
                  class={`flex justify-between pl-4 pr-2 rounded-full py-2 gap-2 items-center  ${this.headerTransparent && this.hasScrolled ? 'bg-header' : this.headerTransparent ? 'bg-primary' : 'bg-header'
                    }`}
                >
                  <span class="text-sm text-gray-400">Let’s link your card</span>
                  <div
                    onClick={this.addCardPopup}
                    class="flex text-nowrap add-new-card-button cursor-pointer bg-white py-2 rounded-full px-3 items-center gap-1 text-xs font-bold text-sb-primary"
                  >
                    <img alt="" src={assetPath + 'icons/plusRoundedBlue.svg'} />
                    ADD NEW CARD
                  </div>
                </div>
              }
            />
            <If
              condition={this.availableCards.length > 0 || (!!this.selectedCardName && this.selectedCardName !== 'undefined')}
              children={
                <div
                  class={`${this.headerTransparent && this.hasScrolled ? 'bg-header' : this.headerTransparent ? 'bg-primary' : 'bg-header'} ${this.selectedCard.cashBackCard ? 'py-3 px-4' : 'px-5 py-2 md:pt-1 md:pb-2'
                    }  relative flex gap-4 rounded-full items-center`}
                >
                  <div class="md:hidden flex-shrink-0">
                    <img onError={this.onCardFaciaError} width={50} height={40} alt="" src={this.cardFaciaUrl + this.selectedCard.imageUrl} />
                  </div>
                  <div class={`flex-1 min-w-0 flex ${this.switchEnabled ? 'cursor-pointer' : ''} ${this.selectedCard.cashBackCard ? 'flex-row items-center gap-10' : 'flex-col'}`} onClick={this.showCardList}>
                    <div class="flex gap-1 items-center">
                      <p class="font-medium text-xs md:text-sm tracking-[3px] card-title max-w-[120px] truncate">{this.selectedCard?.subProgram?.replaceAll("_", " ") || this.selectedCard?.alter_name?.replaceAll("_", " ") || this.selectedCardName} </p>
                      <If condition={this.switchEnabled} children={<img alt="profile dropdown" class="w-3" src={`${assetPath}icons/dropdownIcon.svg`} />} />
                    </div>
                    <p class="font-medium text-xs mt-0.5 text-gray-400">****&nbsp;&nbsp;****&nbsp;&nbsp;{this.getLastFourDigits()}</p>
                  </div>
                  <If condition={!this.selectedCard.cashBackCard} children={<component-separator />} />
                  <If
                    condition={!this.selectedCard.cashBackCard}
                    children={
                      <div class="flex-shrink-0">
                        <p class="font-bold text-left text-sm md:text-base">{formatNumberIN(this.selectedCard.points_available) || this.selectedCardPoints}</p>
                        <div class="flex gap-0.5 text-xs items-center mt-0.5 text-gray-400 whitespace-nowrap">
                          Reward Points
                          {/* <tool-tip assetPath={assetPath} header="Tooltip Header" description="Tooltip information">
                    <img alt="profile dropdown" src={`${assetPath}icons/infoRounded.svg`} />
                  </tool-tip> */}
                        </div>
                      </div>
                    }
                  />
                </div>
              }
            />
            <If
              condition={this.showCards}
              children={
                <div class="">
                  <linear-gradient-popup onPopupClose={this.onPoupClose} open={this.showCards} class="md:hidden" assetPath={assetPath}>
                    <div class="md:hidden py-3 px-4 font-semibold border-b border-gray-700 border-solid">Switch to</div>
                    <map-component
                      class="overflow-y-scroll max-h-[350px] block no-scrollbar"
                      items={this.availableCards}
                      renderItem={(item: any, index: number) => <unification-card card={item} key={index} onSelectCard={this.handleCardSelected} />}
                    />
                    <div onClick={this.addCardPopup} class="py-5 md:py-3 cursor-pointer font-semibold flex gap-2 md:justify-center items-center">
                      <img src={assetPath + 'icons/plusRounded.svg'} />
                      Add New Card
                    </div>
                  </linear-gradient-popup>
                  <div class="absolute hidden slide-in-bottom md:block z-10 left-0 right-0 top-16 ">
                    <linear-gradrient rounded="rounded-xl">
                      <div class="cards-drop-down-gradient rounded-xl">
                        <div class="py-3 px-4 font-semibold border-b border-slate-500 border-solid">Switch to</div>
                        <div class="overflow-y-scroll max-h-[230px] no-scrollbar">
                          <map-component
                            items={this.availableCards}
                            renderItem={(item: any, index: number) => <unification-card card={item} key={index} onSelectCard={this.handleCardSelected} />}
                          />
                        </div>
                        <div onClick={this.addCardPopup} class="py-3 cursor-pointer font-semibold flex gap-2 justify-center items-center">
                          <span class="rounded-full border-solid border-white w-6 flex justify-center ites-center h-6 leading-[19px] border-2">+</span>
                          Add New Card
                        </div>
                      </div>
                    </linear-gradrient>
                  </div>
                </div>
              }
            />
          </div>
        </linear-gradrient>
        <If
          condition={this.showAddCardPopup}
          children={
            <add-card
              addCardTitle={this.addCardTitle}
              customerInfo={this.customerInfo}
              onCloseAddCard={this.onCloseAddCard}
              onCancelAddCard={this.onCancelAddCard}
              assetPath={this.assetPath}
              isAddCardRequired={this.isAddCardRequired}
            />
          }
        />
      </Host>
    );
  }
}
